import { TRPCClientError, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type AppRouter from '@omniwound/api';

import { isApiErrorEnvelope } from './api-error';

/*
 * Client-side tRPC link that recovers the BFF "demote-then-recover" error
 * contract. `globalErrorMiddleware` returns a `{ httpStatus, errors }` envelope
 * as a procedure's *output* instead of re-throwing. This link inspects every
 * successful response, detects that envelope, and converts it back into a
 * thrown `TRPCClientError` so it reaches React Query's `onError` instead of
 * firing a fake-success `onSuccess`.
 *
 * Must sit ABOVE (before) the terminating `httpBatchLink` so it observes the
 * response on the way back up.
 */
export const errorRecoveryLink =
  (): TRPCLink<AppRouter> =>
  () =>
  ({ op, next }) =>
    observable((observer) => {
      const subscription = next(op).subscribe({
        next(value) {
          // value.result is a union: a query/mutation data result
          // ({ data; type?: 'data' } — `type` is OPTIONAL, so it may be absent),
          // subscription frames ({ type: 'started' | 'stopped'; data?: never }),
          // and TRPCConnectionState (no `data`). Treat "type absent or 'data'" as
          // a data result, and narrow with `'data' in result` so reading `.data`
          // is type-safe across the whole union.
          const result = value.result;
          const isDataResult = !('type' in result) || result.type === 'data';
          const data = isDataResult && 'data' in result ? result.data : undefined;
          if (isApiErrorEnvelope(data)) {
            observer.error(
              new TRPCClientError(data.errors[0].message, {
                meta: { apiError: data }, // expose code/httpStatus to callers that want it
              }),
            );
            return;
          }
          observer.next(value);
        },
        error: (err) => observer.error(err),
        complete: () => observer.complete(),
      });
      // Propagate teardown so cancellation/unmount unsubscribes upstream work.
      return () => subscription.unsubscribe();
    });
